/**
 * Recording a payment.
 *
 * One dialog for two entry points, because it is one act: the ledger records a
 * payment against whatever the customer has open, and the document screen
 * records one against the invoice on screen. The difference is a prefilled
 * field, not a different form.
 *
 * **A payment with no document is on account, and that is a real state** — a
 * transfer arrives with a remittance advice nobody has matched yet. The
 * backend accepts it, deducts it from the balance and frees the credit, and
 * this form has to make it reachable rather than forcing a wrong imputation.
 * An earlier version required a document, which taught the desk to guess.
 *
 * The amount is typed, kept and sent as a string. Parsing it to a number to
 * validate it would round it before the server ever saw it.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction } from "@hardauto/core-data";
import { money } from "@hardauto/core-i18n";
import { Field } from "@hardauto/core-forms";
import { Button, Dialog, Input, Select, useToast } from "@hardauto/core-ui";

import type { BillingApi, FinancialDocument, PaymentMethod } from "./api";
import { NAMESPACE } from "./i18n";
import { METHODS } from "./status";

export interface SettleDialogProps {
  api: BillingApi;
  open: boolean;
  onClose: () => void;
  /** Recorded against this customer. */
  clientId: string;
  clientLabel: string;
  /**
   * What the payment may be applied to.
   *
   * Empty is allowed and means the select offers only "on account" — the
   * ledger opens the dialog before knowing which documents are open.
   */
  documents?: readonly FinancialDocument[];
  /** Preselected document, when the dialog was opened from one. */
  documentId?: string;
  onRecorded: () => void;
}

/** Today, as the date input wants it. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function SettleDialog({
  api,
  open,
  onClose,
  clientId,
  clientLabel,
  documents = [],
  documentId,
  onRecorded,
}: SettleDialogProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const action = useAction();
  const toast = useToast();

  const [amount, setAmount] = useState("");
  const [receivedOn, setReceivedOn] = useState(today);
  const [method, setMethod] = useState<PaymentMethod>("transfer");
  const [reference, setReference] = useState("");
  const [target, setTarget] = useState(documentId ?? "");
  const [error, setError] = useState<string | null>(null);

  // Reopening the dialog starts a new payment. Without this, the second one
  // opens carrying the first's amount and reference, and a distracted user
  // records it — which is the one mistake in this dialog that moves money.
  useEffect(() => {
    if (!open) return;
    setTarget(documentId ?? "");
    setAmount(
      documentId ? (documents.find((row) => row.id === documentId)?.outstanding ?? "") : "",
    );
    setReceivedOn(today());
    setMethod("transfer");
    setReference("");
    setError(null);
  }, [open, documentId, documents]);

  /**
   * The documents a payment can actually be applied to.
   *
   * Anything with nothing left to pay is out, and so is every credit note:
   * its outstanding is negative, so offering it would prefill the amount with
   * a negative figure and invite a payment *against* money the customer is
   * owed. A credit note is consumed by the balance, not settled.
   */
  const payable = useMemo(
    () => documents.filter((row) => Number(row.outstanding) > 0),
    [documents],
  );

  /** What is still owed on the selected document, for the ceiling check. */
  const selected = useMemo(
    () => payable.find((row) => row.id === target) ?? null,
    [payable, target],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("settle.title")}
      description={t("settle.description")}
      closeLabel={t("settle.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("settle.cancel")}</Button>
          <Button
            variant="primary"
            busy={action.isBusy("settle")}
            busyLabel={t("settle.submit")}
            onClick={() =>
              void action.run("settle", async () => {
                setError(null);
                if (!amount.trim()) return;
                // Compared as numbers, sent as a string: this is a warning
                // about a typo, and the server clamps whatever gets through.
                if (selected && Number(amount) > Number(selected.outstanding)) {
                  setError(t("settle.tooMuch"));
                  return;
                }
                try {
                  await api.settle({
                    client_id: clientId,
                    amount: amount.trim(),
                    received_on: receivedOn,
                    method,
                    document_id: target || null,
                    reference: reference.trim(),
                  });
                  toast.show(t("settle.done"), { tone: "success" });
                  onRecorded();
                  onClose();
                } catch {
                  setError(t("settle.failed"));
                }
              })
            }
          >
            {t("settle.submit")}
          </Button>
        </>
      }
    >
      <div className="hardauto-form">
        <Field label={t("settle.client")}>
          {(props) => <Input {...props} value={clientLabel} readOnly />}
        </Field>
        <Field label={t("settle.amount")} required error={error ?? undefined}>
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("settle.receivedOn")} required>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={receivedOn}
              onChange={(event) => setReceivedOn(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("settle.method")}>
          {(props) => (
            <Select
              {...props}
              value={method}
              onChange={(event) => setMethod(event.target.value as PaymentMethod)}
            >
              {METHODS.map((value) => (
                <option key={value} value={value}>
                  {t(`method.${value}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("settle.document")}>
          {(props) => (
            <Select
              {...props}
              value={target}
              onChange={(event) => {
                setTarget(event.target.value);
                const document = payable.find((row) => row.id === event.target.value);
                if (document) setAmount(document.outstanding);
              }}
            >
              <option value="">{t("settle.onAccount")}</option>
              {payable.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.number} — {money(row.outstanding, locale)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("settle.reference")} hint={t("settle.referenceHint")}>
          {(props) => (
            <Input
              {...props}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
